import { useContext, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { GlobalContext } from '../context/GlobalContext';
import useFetch, { callAPI, type API } from '../hooks/api';
import { ContainerSpinner } from '../components/Spinner';
import { TwoFactorAuthV2 } from '../components/2FA';
import PhoneInput from '../components/PhoneInput';
import Input from '../components/Input';
import Notice from '../components/Notice';
import Select from '../components/Select';
import { Button } from '../shadcn/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../shadcn/Dialog';
import { ArrowUp, Check, Plus, X, Landmark, Phone, Store, Loader2 } from 'lucide-react';
import { BANK_ACCOUNT_INTERPOLATION, COUNTRY_LABELS } from '../utils/constants';
import {
	validate_phone_number,
	validate_email,
	validate_bank_account_number,
	validate_identity_number,
	validate_interbank_code,
	type IdentityType
} from '../utils/validate';

type ProfileData = API['https://api.krece.app/storeprofile/perfil/']['GET']['response'];
const actionClassName =
	'shrink-0 rounded-md py-2 text-sm font-semibold text-primary-300 hover:text-primary-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500 light:text-primary-700 light:hover:text-primary-800 disabled:opacity-50';
const iconButtonClassName =
	'flex size-9 shrink-0 items-center justify-center rounded-lg border border-neutral-800 text-neutral-300 hover:border-primary-500 hover:bg-primary-500/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500 disabled:opacity-50';

export default function Perfil() {
	const { userData } = useContext(GlobalContext);
	const {
		data: profile,
		loading,
		error,
		refetch
	} = useFetch('https://api.krece.app/storeprofile/perfil/', 'GET', !!userData?.master_user);

	if (!userData) return <ContainerSpinner size={3} />;
	if (!userData.master_user) return <Navigate to="/" replace />;

	return (
		<div className="mx-auto w-full max-w-7xl p-4 md:p-8">
			<header className="mb-6">
				<h1 className="text-2xl font-semibold tracking-tight text-neutral-100">Perfil de la tienda</h1>
				<p className="mt-2 text-sm text-neutral-400">Administra los datos y las condiciones de tu cuenta.</p>
			</header>
			{error && (
				<div className="mb-6 space-y-3">
					<Notice variant="error" message={typeof error === 'string' ? error : 'Error cargando perfil'} />
					<Button variant="secondary" className="!w-auto" onClick={refetch}>
						Reintentar
					</Button>
				</div>
			)}
			{(loading || (!profile && !error)) && <PerfilSkeleton />}
			{profile && !loading && (
				<>
					<section className="mb-6 flex items-center gap-4 rounded-xl border border-neutral-800 bg-background p-5 md:p-6">
						<div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-primary-500/10 text-primary-300 light:text-primary-700">
							<Store className="size-6" />
						</div>
						<div className="min-w-0">
							<h2 className="break-words text-lg font-semibold text-neutral-100">{profile.store_name}</h2>
							<p className="mt-1 text-sm text-neutral-400">
								Cuenta principal · {COUNTRY_LABELS[profile.country as keyof typeof COUNTRY_LABELS] || profile.country}
							</p>
						</div>
					</section>
					<div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
						<div className="min-w-0 space-y-6">
							<GeneralInfoCard profile={profile} refetch={refetch} />
							<ForwardEmailsField profile={profile} />
							<BankInfoCard profile={profile} refetch={refetch} />
						</div>
						<ConditionsCard profile={profile} refetch={refetch} />
					</div>
					{profile.last_modified_date && (
						<p className="mt-6 text-xs text-neutral-400">
							Última modificación ·{' '}
							{new Date(profile.last_modified_date).toLocaleDateString('es', { day: 'numeric', month: 'long', year: 'numeric' })}
						</p>
					)}
				</>
			)}
		</div>
	);
}

function PerfilSkeleton() {
	return (
		<div className="space-y-6" role="status" aria-label="Cargando perfil">
			<div className="h-24 animate-pulse rounded-xl bg-neutral-900/50" />
			<div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
				<div className="space-y-6">
					<div className="h-48 animate-pulse rounded-xl bg-neutral-900/50" />
					<div className="h-36 animate-pulse rounded-xl bg-neutral-900/50" />
					<div className="h-64 animate-pulse rounded-xl bg-neutral-900/50" />
				</div>
				<div className="h-48 animate-pulse rounded-xl bg-neutral-900/50" />
			</div>
		</div>
	);
}

function Card({ children, title, headerRight }: { children: React.ReactNode; title: string; headerRight?: React.ReactNode }) {
	return (
		<section className="min-w-0 rounded-xl border border-neutral-800 bg-background p-5 md:p-6">
			<header className="mb-1 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
				<h2 className="text-base font-semibold text-neutral-100">{title}</h2>
				{headerRight}
			</header>
			{children}
		</section>
	);
}

function InfoField({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
	return (
		<div className="min-w-0 py-4">
			<p className="text-sm text-neutral-400">{label}</p>
			<p className={`mt-1.5 break-words font-medium text-neutral-100 ${mono ? 'font-mono text-sm' : 'text-sm'}`}>
				{value || '—'}
			</p>
		</div>
	);
}

function GeneralInfoCard({ profile, refetch }: { profile: ProfileData; refetch: () => void }) {
	const [phoneDialogOpen, setPhoneDialogOpen] = useState(false);
	return (
		<Card title="Datos de contacto">
			<div className="divide-y divide-neutral-800">
				<InfoField label="Correo de la cuenta" value={profile.email} />
				<div className="flex items-center justify-between gap-3">
					<InfoField label="Teléfono" value={profile.phone_number} />
					<button className={actionClassName} title="Cambiar teléfono" onClick={() => setPhoneDialogOpen(true)}>
						Cambiar
					</button>
				</div>
			</div>
			{phoneDialogOpen && (
				<EditPhoneDialog
					open
					onClose={() => setPhoneDialogOpen(false)}
					currentPhone={profile.phone_number}
					country={profile.country}
					refetch={refetch}
				/>
			)}
		</Card>
	);
}

function ForwardEmailsField({ profile }: { profile: ProfileData }) {
	const { userData } = useContext(GlobalContext);
	const [emails, setEmails] = useState<string[]>(profile.forward_email_address || []);
	const [newEmail, setNewEmail] = useState('');
	const [adding, setAdding] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');
	const submitting = useRef(false);
	const persist = async (updatedList: string[]) => {
		if (submitting.current) return;
		submitting.current = true;
		setSaving(true);
		setError('');
		try {
			const [data, err] = await callAPI(
				'https://api.krece.app/storeprofile/perfil/forward-email/',
				'PATCH',
				{ forward_email_address: updatedList },
				userData,
				profile.country
			);
			if (data && !err) {
				setEmails(data.forward_email_address);
				setNewEmail('');
				setAdding(false);
			} else setError(err || 'Error guardando cambios');
		} catch {
			setError('Error guardando cambios');
		} finally {
			submitting.current = false;
			setSaving(false);
		}
	};
	const addEmail = () => {
		const trimmed = newEmail.trim();
		if (!validate_email(trimmed)) {
			setError('Correo electrónico inválido');
			return;
		}
		if (emails.includes(trimmed)) {
			setError('Este correo ya está en la lista');
			return;
		}
		persist([...emails, trimmed]);
	};
	return (
		<Card
			title="Correos para recibir reportes"
			headerRight={
				<button className={`${actionClassName} flex items-center gap-1`} disabled={saving} onClick={() => setAdding(true)}>
					<Plus className="size-4" />
					Agregar
				</button>
			}
		>
			<div className="divide-y divide-neutral-800">
				{emails.map((email, index) => (
					<div key={email} className="flex items-center justify-between gap-3 py-4">
						<span className="min-w-0 break-all text-sm text-neutral-200">{email}</span>
						<button
							className={iconButtonClassName}
							aria-label={`Quitar ${email}`}
							disabled={saving}
							onClick={() => persist(emails.filter((_, i) => i !== index))}
						>
							<X className="size-4" />
						</button>
					</div>
				))}
			</div>
			{emails.length === 0 && <p className="py-4 text-sm text-neutral-400">No hay correos adicionales.</p>}
			{adding && (
				<form
					className="my-3 space-y-3"
					onSubmit={(event) => {
						event.preventDefault();
						addEmail();
					}}
				>
					<input
						autoFocus
						type="email"
						aria-label="Correo para reportes"
						placeholder="correo@ejemplo.com"
						value={newEmail}
						disabled={saving}
						onChange={(event) => {
							setNewEmail(event.target.value);
							setError('');
						}}
						className="w-full rounded-lg border border-neutral-800 bg-neutral-925 px-3 py-2.5 text-sm text-neutral-100 placeholder:text-neutral-400 focus:border-primary-500"
					/>
					<div className="flex gap-2">
						<Button type="submit" variant="primary" loading={saving} disabled={saving || !newEmail.trim()}>
							Guardar
						</Button>
						<Button
							variant="secondary"
							disabled={saving}
							onClick={() => {
								setAdding(false);
								setError('');
							}}
						>
							Cancelar
						</Button>
					</div>
				</form>
			)}
			{error && <Notice message={error} variant="error" />}
			{saving && (
				<p className="my-2 flex items-center gap-2 text-sm text-neutral-400">
					<Loader2 className="size-4 animate-spin" />
					Guardando...
				</p>
			)}
			<p className="mt-2 border-t border-neutral-800 pt-4 text-xs leading-relaxed text-neutral-400">
				Estos correos reciben una copia de los reportes de tu tienda.
			</p>
		</Card>
	);
}

function BankInfoCard({ profile, refetch }: { profile: ProfileData; refetch: () => void }) {
	const [dialogOpen, setDialogOpen] = useState(false);
	const bank = profile.bank_account;
	return (
		<Card
			title="Cuenta bancaria"
			headerRight={
				profile.has_pending_bank_request ? (
					<span className="text-sm text-neutral-400">Solicitud pendiente</span>
				) : (
					<button className={actionClassName} title="Solicitar cambio de cuenta" onClick={() => setDialogOpen(true)}>
						Solicitar cambio
					</button>
				)
			}
		>
			{bank?.bank_name ? (
				<>
					<div className="flex items-center gap-3 py-5">
						<div className="rounded-lg border border-neutral-800 bg-neutral-925 p-3">
							<Landmark className="size-5 text-neutral-300" />
						</div>
						<div className="min-w-0">
							<p className="break-words text-sm font-semibold text-neutral-100">{bank.bank_name}</p>
							<p className="mt-1 text-xs text-neutral-400">
								{bank.account_type ? `Cuenta ${bank.account_type}` : 'Cuenta bancaria'}
							</p>
						</div>
					</div>
					<div className="divide-y divide-neutral-800">
						<InfoField label="Titular" value={bank.account_name} />
						<InfoField label="Número de cuenta" value={bank.account_number} mono />
						{bank.document_type && <InfoField label={bank.document_type.toUpperCase()} value={bank.identification_number} />}
						{bank.interbank_code && <InfoField label="Código interbancario" value={bank.interbank_code} mono />}
					</div>
				</>
			) : (
				<p className="py-6 text-sm text-neutral-400">No hay información bancaria disponible.</p>
			)}
			<p className="mt-3 border-t border-neutral-800 pt-4 text-xs text-neutral-400">
				Los cambios de cuenta requieren revisión de Krece.
			</p>
			{dialogOpen && (
				<BankChangeDialog
					open
					onClose={() => setDialogOpen(false)}
					country={profile.country}
					currentPhone={profile.phone_number}
					refetch={refetch}
				/>
			)}
		</Card>
	);
}

function ConditionsCard({ profile, refetch }: { profile: ProfileData; refetch: () => void }) {
	const [dialogOpen, setDialogOpen] = useState(false);
	const [approvedLimit, setApprovedLimit] = useState<number | null>(null);
	const limit = approvedLimit ?? profile.limit_count;
	return (
		<aside className="min-w-0 space-y-4">
			<Card title="Condiciones de tu tienda">
				<div className="divide-y divide-neutral-800">
					<div className="flex items-center justify-between gap-3 py-5">
						<div>
							<p className="text-sm text-neutral-400">Límite diario de ventas</p>
							<div className="mt-2 flex flex-wrap items-baseline gap-1.5">
								<span className="text-2xl font-semibold tracking-tight text-neutral-100">{limit ?? 'No disponible'}</span>
								{limit !== null && <span className="text-xs text-neutral-400">ventas por día</span>}
							</div>
						</div>
						<button
							className={iconButtonClassName}
							title="Solicitar aumento de límite"
							aria-label="Solicitar aumento de límite"
							onClick={() => setDialogOpen(true)}
						>
							<ArrowUp className="size-4" />
						</button>
					</div>
					<InfoField
						label="Sucursales asociadas"
						value={`${profile.sub_stores_count} ${profile.sub_stores_count === 1 ? 'sucursal' : 'sucursales'}`}
					/>
				</div>
			</Card>
			<p className="px-1 text-xs leading-relaxed text-neutral-400">
				El límite aplica a las ventas de tu tienda y sus sucursales.
			</p>
			{dialogOpen && (
				<DailyLimitDialog
					profile={profile}
					onApproved={setApprovedLimit}
					onClose={(approved) => {
						setDialogOpen(false);
						if (approved) refetch();
					}}
				/>
			)}
		</aside>
	);
}

function DailyLimitDialog({
	profile,
	onApproved,
	onClose
}: {
	profile: ProfileData;
	onApproved: (limit: number) => void;
	onClose: (approved: boolean) => void;
}) {
	const { userData } = useContext(GlobalContext);
	const {
		data: limitStatus,
		loading,
		error,
		refetch,
		fetched
	} = useFetch('https://api.krece.app/storeprofile/request_daily_limit_increase/', 'GET', true, {
		master_store_email: profile.email
	});
	const [requesting, setRequesting] = useState(false);
	const [requestError, setRequestError] = useState('');
	const [result, setResult] = useState<
		API['https://api.krece.app/storeprofile/request_daily_limit_increase/']['POST']['response'] | null
	>(null);
	const submitting = useRef(false);
	const submitted = useRef(false);
	const [reconciliation, setReconciliation] = useState<'idle' | 'loading' | 'failed' | 'done'>('idle');
	const [reconciledStatus, setReconciledStatus] = useState<
		API['https://api.krece.app/storeprofile/request_daily_limit_increase/']['GET']['response'] | null
	>(null);
	const [reconciliationError, setReconciliationError] = useState('');
	const status = reconciliation === 'idle' ? limitStatus : reconciledStatus;
	const statusLoading = loading || reconciliation === 'loading';
	const statusError = reconciliationError || error;
	const currentLimit = status?.current_limit;
	const hasLimit = typeof currentLimit === 'number' && Number.isFinite(currentLimit);
	const sold = status?.devices_sold;
	const hasSales = typeof sold === 'number' && Number.isFinite(sold);
	// The POST evaluates the authoritative counter, not only the limit_reached flag.
	const canRequest = hasLimit && hasSales && sold >= currentLimit;
	const close = () => {
		if (!submitting.current) onClose(submitted.current);
	};
	const reconcileStatus = async () => {
		// Hold the guard across token refresh as well as the GET, not only fetch loading.
		submitting.current = true;
		setReconciliation('loading');
		setReconciliationError('');
		try {
			const [data, err] = await callAPI(
				'https://api.krece.app/storeprofile/request_daily_limit_increase/',
				'GET',
				{ master_store_email: profile.email },
				userData,
				profile.country
			);
			if (!data || err) {
				setReconciliationError(err || 'No pudimos consultar el límite actualizado. Reintenta la consulta.');
				setReconciliation('failed');
				return;
			}
			setReconciledStatus(data);
			if (typeof data.current_limit === 'number' && Number.isFinite(data.current_limit)) onApproved(data.current_limit);
			setReconciliation('done');
		} catch {
			setReconciliationError('No pudimos consultar el límite actualizado. Reintenta la consulta.');
			setReconciliation('failed');
		} finally {
			submitting.current = false;
		}
	};
	const requestIncrease = async () => {
		if (!canRequest || submitting.current || result || statusLoading || statusError || !fetched || reconciliation === 'failed')
			return;
		submitting.current = true;
		submitted.current = true;
		setRequesting(true);
		setRequestError('');
		try {
			const [data, err] = await callAPI(
				'https://api.krece.app/storeprofile/request_daily_limit_increase/',
				'POST',
				{ master_store_email: profile.email },
				userData,
				profile.country
			);
			if (data && !err && Number.isFinite(data.new_limit) && data.new_limit > currentLimit) {
				setResult(data);
				onApproved(data.new_limit);
			} else {
				setRequestError(err || 'No pudimos confirmar el resultado. Consulta el límite actualizado antes de volver a solicitar.');
				await reconcileStatus();
			}
		} catch {
			setRequestError('No pudimos confirmar el resultado. Consulta el límite actualizado antes de volver a solicitar.');
			await reconcileStatus();
		} finally {
			submitting.current = false;
			setRequesting(false);
		}
	};
	return (
		<Dialog open onOpenChange={(open) => !open && close()}>
			<DialogContent
				className="max-h-[90vh] overflow-y-auto sm:max-w-md"
				showCloseButton={!requesting && reconciliation !== 'loading'}
			>
				<DialogHeader>
					<DialogTitle className="pr-5">{result ? 'Tu límite aumentó' : 'Solicitar aumento de límite'}</DialogTitle>
					<DialogDescription>
						{result
							? 'Ya puedes continuar vendiendo con el nuevo límite.'
							: 'Evaluaremos si tu tienda puede vender más equipos por día.'}
					</DialogDescription>
				</DialogHeader>
				{result ? (
					<>
						<div className="flex size-11 items-center justify-center rounded-full bg-primary-500/10 text-primary-300 light:text-primary-700">
							<Check className="size-6" />
						</div>
						<div className="space-y-3 rounded-lg bg-neutral-925 p-4">
							<div className="flex justify-between gap-4 text-sm">
								<span className="text-neutral-400">Antes</span>
								<strong>{result.prior_limit ?? '—'} ventas por día</strong>
							</div>
							<div className="flex justify-between gap-4 text-sm">
								<span className="text-neutral-400">Ahora</span>
								<strong>{result.new_limit} ventas por día</strong>
							</div>
						</div>
						<Button variant="primary" onClick={close}>
							Entendido
						</Button>
					</>
				) : (
					<>
						{(statusLoading || !fetched) && (
							<div className="flex items-center gap-3 py-5 text-sm text-neutral-400">
								<Loader2 className="size-5 animate-spin" />
								Consultando el límite actual...
							</div>
						)}
						{!statusLoading &&
							fetched &&
							(statusError ? (
								<>
									<Notice
										variant="error"
										message={typeof statusError === 'string' ? statusError : 'No pudimos consultar el límite actual.'}
									/>
									<Button variant="secondary" onClick={reconciliation === 'idle' ? refetch : reconcileStatus}>
										Reintentar consulta
									</Button>
								</>
							) : (
								<>
									<div className="space-y-3 rounded-lg bg-neutral-925 p-4">
										<div className="flex justify-between gap-4 text-sm">
											<span className="text-neutral-400">Límite actual</span>
											<strong>{hasLimit ? `${currentLimit} ventas por día` : 'No disponible'}</strong>
										</div>
										<div className="flex justify-between gap-4 text-sm">
											<span className="text-neutral-400">Ventas de hoy</span>
											<strong>{hasSales ? sold : 'No disponible'}</strong>
										</div>
									</div>
									<p className="text-sm leading-relaxed text-neutral-400">
										{!hasLimit || !hasSales
											? 'No hay información suficiente para solicitar un aumento. Contacta a soporte.'
											: !canRequest
												? 'Aún tienes ventas disponibles. Puedes solicitar un aumento cuando alcances tu límite diario.'
												: 'No necesitas indicar un nuevo monto. Si tu tienda califica, el aumento se aplica automáticamente.'}
									</p>
								</>
							))}
						{requestError && <Notice variant="error" message={requestError} />}
						<DialogFooter>
							<Button variant="secondary" disabled={requesting || reconciliation === 'loading'} onClick={close}>
								Cerrar
							</Button>
							<Button
								variant="primary"
								disabled={requesting || statusLoading || !fetched || !!statusError || !canRequest || reconciliation === 'failed'}
								loading={requesting}
								onClick={requestIncrease}
							>
								{requesting ? 'Evaluando solicitud' : 'Solicitar aumento'}
							</Button>
						</DialogFooter>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
}

function EditPhoneDialog({
	open,
	onClose,
	currentPhone,
	country,
	refetch
}: {
	open: boolean;
	onClose: () => void;
	currentPhone: string;
	country: string;
	refetch: () => void;
}) {
	const { userData } = useContext(GlobalContext);
	const [step, setStep] = useState<1 | 2 | 3>(1);
	const [newPhone, setNewPhone] = useState('');
	const [phoneConfirmed, setPhoneConfirmed] = useState(false);
	const [verificationToken, setVerificationToken] = useState('');
	const [submitLoading, setSubmitLoading] = useState(false);
	const [error, setError] = useState('');
	const [success, setSuccess] = useState(false);
	const submitting = useRef(false);

	const handleClose = () => {
		if (submitting.current) return;
		onClose();
	};

	const handleSubmitPhoneChange = async () => {
		if (submitting.current || step !== 3 || !verificationToken || success) return;
		submitting.current = true;
		setError('');
		setSubmitLoading(true);
		try {
			const [data, err] = await callAPI(
				'https://api.krece.app/storeprofile/perfil/phone/',
				'PATCH',
				{ phone_number: newPhone, verification_token: verificationToken },
				userData,
				country
			);
			if (data && !err) {
				setSuccess(true);
				refetch();
			} else {
				setError(err || 'Error actualizando teléfono');
			}
		} catch {
			setError('Error actualizando teléfono');
		} finally {
			submitting.current = false;
			setSubmitLoading(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={(value) => !value && handleClose()}>
			<DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<Phone className="size-5" />
						Cambiar teléfono
					</DialogTitle>
					<DialogDescription>
						{step === 1 && 'Verifique su identidad por SMS o WhatsApp en el número actual.'}
						{step === 2 && 'Ingrese y verifique el nuevo número.'}
						{step === 3 && 'Confirme el cambio de teléfono.'}
					</DialogDescription>
				</DialogHeader>
				<div className="space-y-4">
					{step === 1 &&
						(currentPhone ? (
							<TwoFactorAuthV2
								key="current-phone"
								country={country}
								methods={[
									{
										type: 'sms',
										phoneNumber: currentPhone,
										phoneCountry: country
									},
									{
										type: 'whatsapp',
										phoneNumber: currentPhone,
										phoneCountry: country
									}
								]}
								onSuccess={() => setStep(2)}
							/>
						) : (
							<Notice variant="warning" message="No hay un teléfono asociado. Contacte a soporte para actualizarlo." />
						))}
					{step === 2 &&
						(!phoneConfirmed ? (
							<div className="space-y-3">
								<PhoneInput country={country} value={newPhone} onChange={setNewPhone} />
								<Button
									variant="primary"
									disabled={!validate_phone_number(newPhone, country) || newPhone === currentPhone}
									onClick={() => setPhoneConfirmed(true)}
								>
									Continuar
								</Button>
							</div>
						) : (
							<div className="space-y-3">
								<TwoFactorAuthV2
									key={newPhone}
									country={country}
									issueVerificationToken
									methods={[
										{
											type: 'sms',
											phoneNumber: newPhone,
											phoneCountry: country
										},
										{
											type: 'whatsapp',
											phoneNumber: newPhone,
											phoneCountry: country
										}
									]}
									onSuccess={(_method, _destination, token) => {
										if (!token) return;
										setVerificationToken(token);
										setStep(3);
									}}
								/>
								<Button variant="secondary" onClick={() => setPhoneConfirmed(false)}>
									Cambiar número
								</Button>
							</div>
						))}
					{step === 3 && !success && (
						<div className="space-y-3">
							<InfoField label="Número actual" value={currentPhone} />
							<InfoField label="Nuevo número" value={newPhone} />
							<Button variant="primary" loading={submitLoading} disabled={submitLoading} onClick={handleSubmitPhoneChange}>
								Confirmar cambio
							</Button>
						</div>
					)}
					{success && <Notice message="Teléfono actualizado exitosamente" variant="success" />}
					{error && <Notice message={error} variant="error" />}
				</div>
			</DialogContent>
		</Dialog>
	);
}

// ─── Bank Change Dialog ──────────────────────────────────────────────────────

function BankChangeDialog({
	currentPhone,
	open,
	onClose,
	country,
	refetch
}: {
	open: boolean;
	onClose: () => void;
	country: string;
	refetch: () => void;
	currentPhone: string;
}) {
	const { userData, CountrySettings } = useContext(GlobalContext);
	const banks = CountrySettings?.banks?.toSorted() || [];
	const [identityVerified, setIdentityVerified] = useState(false);
	const submitting = useRef(false);

	const [form, setForm] = useState({
		bank_name: '',
		account_name: '',
		account_number: '',
		account_type: '',
		document_type: '' as IdentityType | '',
		identification_number: '',
		interbank_code: ''
	});
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');
	const [success, setSuccess] = useState(false);

	const resetForm = () => {
		setForm({
			bank_name: '',
			account_name: '',
			account_number: '',
			account_type: '',
			document_type: '',
			identification_number: '',
			interbank_code: ''
		});
		setError('');
		setSuccess(false);
	};

	const handleClose = () => {
		if (submitting.current) return;
		resetForm();
		onClose();
	};

	const updateField = (field: string, value: string) => {
		setForm((prev) => ({ ...prev, [field]: value }));
	};

	const documentTypeOptions =
		country === 'venezuela'
			? [
					{ label: 'RIF C.A. (J-)', value: 'rif' },
					{ label: 'Cédula Natural (V-)', value: 'cedula' },
					{ label: 'RIF F.P. (V-)', value: 'firma_personal' },
					{ label: 'Cédula Extranjera', value: 'extranjera' }
				]
			: country === 'rd'
				? [
						{ label: 'Cédula', value: 'cedula' },
						{ label: 'RNC', value: 'rnc' },
						{ label: 'Pasaporte', value: 'passport' }
					]
				: country === 'colombia'
					? [
							{ label: 'Cédula', value: 'cedula' },
							{ label: 'NIT', value: 'nit' }
						]
					: country === 'bolivia'
						? [
								{ label: 'NIT', value: 'nit' },
								{ label: 'Cédula', value: 'cedula' }
							]
						: [
								{ label: 'DNI', value: 'dni' },
								{ label: 'Ficha RUC', value: 'ruc' }
							];

	const accountTypeOptions = [
		{ label: 'Ahorro', value: 'ahorro' },
		{ label: 'Corriente', value: 'corriente' },
		...(country === 'bolivia' ? [{ label: 'Yape', value: 'yape' }] : [])
	];

	const accountNumberInterpolation = BANK_ACCOUNT_INTERPOLATION[country as keyof typeof BANK_ACCOUNT_INTERPOLATION];

	const idPrefix =
		form.document_type === 'rif'
			? 'J-'
			: (form.document_type === 'cedula' || form.document_type === 'firma_personal') && country === 'venezuela'
				? 'V-'
				: form.document_type === 'extranjera' && country === 'venezuela'
					? 'E-'
					: undefined;

	const needsInterbankCode = country === 'peru';

	const isValid =
		form.bank_name &&
		form.account_name.trim().length >= 4 &&
		validate_bank_account_number(form.account_number, country, form.bank_name) &&
		form.account_type &&
		form.document_type &&
		validate_identity_number(form.identification_number, form.document_type as IdentityType, country) &&
		(needsInterbankCode ? validate_interbank_code(form.interbank_code, country) : true);

	const handleSubmit = async () => {
		if (!identityVerified || !isValid || submitting.current || success) return;
		submitting.current = true;
		setError('');
		setSaving(true);
		try {
			const [data, err] = await callAPI(
				'https://api.krece.app/storeprofile/perfil/bank-change-request/',
				'POST',
				{ new_bank_info: form },
				userData,
				country
			);
			if (data && !err) {
				setSuccess(true);
				refetch();
			} else {
				setError(err || 'Error enviando solicitud');
			}
		} catch {
			setError('Error enviando solicitud');
		} finally {
			submitting.current = false;
			setSaving(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
			<DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<Landmark className="size-5" />
						Solicitar cambio de cuenta bancaria
					</DialogTitle>
					<DialogDescription>
						{identityVerified
							? 'Ingrese los datos de la nueva cuenta bancaria.'
							: 'Verifique su identidad por SMS o WhatsApp antes de solicitar el cambio.'}
					</DialogDescription>
				</DialogHeader>

				{!identityVerified ? (
					currentPhone ? (
						<TwoFactorAuthV2
							country={country}
							methods={[
								{
									type: 'sms',
									phoneNumber: currentPhone,
									phoneCountry: country
								},
								{
									type: 'whatsapp',
									phoneNumber: currentPhone,
									phoneCountry: country
								}
							]}
							onSuccess={() => setIdentityVerified(true)}
						/>
					) : (
						<Notice variant="warning" message="No hay un teléfono asociado. Contacte a soporte para actualizarlo." />
					)
				) : (
					<>
						<div className="space-y-4">
							<Notice
								variant="warning"
								message="Esta solicitud será revisada por el equipo de Krece. Los cambios no serán inmediatos."
							/>

							<div className="flex gap-3">
								<Select
									containerClassName="flex-1"
									options={banks.map((b) => ({ label: b, value: b }))}
									onSelectedChange={(val) => {
										const bank = typeof val === 'string' ? val.trim() : '';
										updateField('bank_name', bank);
										if (bank.length > 6) {
											const prefix = bank.slice(0, 6);
											if (prefix.endsWith(' -') && prefix.slice(0, -2).replace(/\D/g, '').length === 4) {
												updateField('account_number', prefix.slice(0, -2).replace(/\D/g, ''));
											}
										}
									}}
									floatingLabel="Banco *"
									placeholder="Seleccionar banco"
								/>
								<Select
									containerClassName="min-w-[140px]"
									options={accountTypeOptions}
									onSelectedChange={(val) => updateField('account_type', val as string)}
									floatingLabel="Tipo de cuenta *"
									placeholder="Seleccionar"
								/>
							</div>

							<Input
								containerClassName="w-full"
								value={form.account_name}
								onChange={(val) => updateField('account_name', val)}
								placeholder="Nombre completo del titular"
								floatingLabel="Titular de la cuenta *"
								allowedChars="numbers_letters_dot_comma"
								validation={(val) => val.trim().length >= 4}
							/>
							<Input
								containerClassName="w-full"
								value={form.account_number}
								onChange={(val) => updateField('account_number', val)}
								placeholder="Número de cuenta"
								floatingLabel="Número de cuenta *"
								allowedChars="numbers_only"
								validation={(val) => validate_bank_account_number(val, country, form.bank_name)}
								interpolation={accountNumberInterpolation}
							/>
							{needsInterbankCode && (
								<Input
									containerClassName="w-full"
									value={form.interbank_code}
									onChange={(val) => updateField('interbank_code', val)}
									placeholder={country === 'peru' ? 'Código CCI' : 'Código interbancario'}
									floatingLabel={country === 'peru' ? 'Código CCI *' : 'Código interbancario *'}
									allowedChars="numbers_only"
									validation={(val) => validate_interbank_code(val, country)}
									interpolation={[
										{ after: 3, char: '-' },
										{ after: 6, char: '-' },
										{ after: 18, char: '-' }
									]}
								/>
							)}
							<Select
								containerClassName="w-full"
								options={documentTypeOptions}
								onSelectedChange={(val) => {
									updateField('document_type', val as string);
									updateField('identification_number', '');
								}}
								floatingLabel="Tipo de documento *"
								placeholder="Seleccionar"
							/>
							{form.document_type && (
								<Input
									containerClassName="w-full"
									value={form.identification_number}
									onChange={(val) => updateField('identification_number', val)}
									placeholder="Número de identificación"
									floatingLabel="Número de identificación *"
									allowedChars={form.document_type === 'passport' ? 'alphanumeric' : 'numbers_only'}
									prefix={idPrefix}
									validation={(val) => validate_identity_number(val, form.document_type as IdentityType, country)}
								/>
							)}

							{error && <Notice message={error} variant="error" />}
							{success && <Notice message="Solicitud enviada exitosamente" variant="success" />}
						</div>

						<DialogFooter>
							<Button variant="secondary" onClick={handleClose} disabled={saving}>
								Cancelar
							</Button>
							<Button variant="primary" onClick={handleSubmit} loading={saving} disabled={saving || !isValid || success}>
								Enviar solicitud
							</Button>
						</DialogFooter>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
}
